import React, { useState, useEffect } from "react";
import { Card, Table, Button, Space, Tooltip, Modal as AntModal } from "antd";
import { LogoutOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useGet } from "../../hooks/useApi";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const UserSessions = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sessionsList, setSessionsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logoutModal, setLogoutModal] = useState({
    visible: false,
    sessionId: null,
    deviceName: "",
  });

  // Use GET for fetching sessions
  const { execute: fetchSessions } = useGet(API_ENDPOINTS.USER_SESSIONS, false);

  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Custom logout function
  const customLogout = async (sessionId) => {
    const token = getToken();
    if (!token) {
      Toast.error(t("common.error"));
      throw new Error("Authentication token not found");
    }

    try {
      const url = `${API_ENDPOINTS.USER_SESSIONS_LOGOUT}/${sessionId}`;
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("userSessions.logoutFailed"));
      }

      return result;
    } catch (error) {
      console.error("Logout request failed:", error);
      throw error;
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Navigate to user details
  const goToUserDetails = (userId) => {
    if (userId) {
      navigate(`/users/details/${userId}`);
    } else {
      Toast.error(t("userSessions.userIdNotFound"));
    }
  };

  // Load sessions data
  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await fetchSessions();
      if (response?.status === true && response.data) {
        setSessionsList(response.data);
      } else {
        Toast.error(response?.message || t("userSessions.fetchFailed"));
      }
    } catch (error) {
      console.error("Error loading sessions:", error);
      Toast.error(error.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadSessions();
  }, []);

  // Handle logout confirmation
  const showLogoutConfirm = (sessionId, deviceName) => {
    setLogoutModal({
      visible: true,
      sessionId,
      deviceName,
    });
  };

  // Handle logout action
  const handleLogout = async () => {
    if (!logoutModal.sessionId) return;

    try {
      const response = await customLogout(logoutModal.sessionId);

      if (response?.status === true) {
        Toast.success(response?.message || t("userSessions.logoutSuccess"));

        // Remove the logged out session from list
        setSessionsList((prev) =>
          prev.filter((session) => session.id !== logoutModal.sessionId),
        );

        // Close modal
        setLogoutModal({ visible: false, sessionId: null, deviceName: "" });
      } else {
        Toast.error(response?.message || t("userSessions.logoutFailed"));
      }
    } catch (error) {
      console.error("Error logging out session:", error);
      Toast.error(error.message || t("userSessions.logoutFailed"));
    }
  };

  const columns = [
    {
      title: t("userSessions.userName"),
      dataIndex: "user_name",
      key: "user_name",

      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => goToUserDetails(record.user_id)}
        >
          {text || "N/A"}
        </Button>
      ),
    },
    {
      title: t("userSessions.userEmail"),
      dataIndex: "user_email",
      key: "user_email",
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => goToUserDetails(record.user_id)}
        >
          {text || "N/A"}
        </Button>
      ),
    },
    {
      title: t("userSessions.mobile_number"),
      dataIndex: "mobile_number",
      key: "mobile_number",
      render: (text, record) => {
        const countryCode = record.country_code || "";
        const mobile = record.mobile_number || "";
        if (!countryCode && !mobile) return "N/A";
        return `${countryCode} ${mobile}`.trim();
      },
    },
    {
      title: t("userSessions.deviceName"),
      dataIndex: "device_name",
      key: "device_name",
      render: (text) => text || "N/A",
    },
    {
      title: t("userSessions.deviceId"),
      dataIndex: "device_id",
      key: "device_id",
      render: (text) => text || "N/A",
    },
    {
      title: t("userSessions.lastActivity"),
      dataIndex: "last_activity",
      key: "last_activity",
      render: (date) => formatDate(date),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t("common.view")}>
            <Button
              type="primary"
              className="btn-view"
              icon={<EyeOutlined />}
              onClick={() => goToUserDetails(record.user_id)}
            />
          </Tooltip>
          <Tooltip title={t("userSessions.logout")}>
            <Button
              danger
              type="primary"
              className="btn-logout"
              icon={<LogoutOutlined />}
              onClick={() => showLogoutConfirm(record.id, record.device_name)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={t("userSessions.title")}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadSessions}
            loading={loading}
          >
            {t("common.refresh")}
          </Button>
        }
      >
        <div className="table-responsive">
          <Table
            dataSource={sessionsList}
            columns={columns}
            rowKey="id"
            loading={loading}
            locale={{
              emptyText: t("userSessions.noSessionsFound"),
            }}
            pagination={{
              defaultPageSize: 10,
              pageSizeOptions: ["5", "10", "20", "50"],
              showSizeChanger: true,
              showTotal: (total) =>
                `${t("common.total")} ${total} ${t("userSessions.sessions")}`,
            }}
          />
        </div>
      </Card>

      {/* Logout Confirmation Modal */}
      <AntModal
        title={t("userSessions.confirmLogout")}
        open={logoutModal.visible}
        onOk={handleLogout}
        onCancel={() =>
          setLogoutModal({ visible: false, sessionId: null, deviceName: "" })
        }
        okText={t("common.yes")}
        cancelText={t("common.no")}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t("userSessions.logoutConfirmationMessage")}{" "}
          <strong>{logoutModal.deviceName}</strong>?
        </p>
      </AntModal>
    </>
  );
};

export default UserSessions;
